import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { toast } from 'sonner';
import { fetchEvents, filterEvents, fallbackCategories, fetchCategoryNames, joinEvent } from '../services/eventsService';
import EventCard from '../components/ui/EventCard';
import { EventGridSkeleton } from '../components/ui/EventCardSkeleton';
import EmptyState from '../components/ui/EmptyState';

// Category colour map, keyed by the names the API actually serves. Unknown
// categories fall back to the brand colour, so adding one server-side does not
// require a frontend change. (This map used to be keyed by a hardcoded
// vocabulary -- Wine & Dine, Exhibition, Evening -- that no category ever
// matched.)
const categoryColors = {
  'All':       { bg: 'bg-primary-50',  text: 'text-primary-600' },
  'Sports':    { bg: 'bg-emerald-50',  text: 'text-emerald-600' },
  'Tech':      { bg: 'bg-blue-50',     text: 'text-blue-600' },
  'Arts':      { bg: 'bg-purple-50',   text: 'text-purple-600' },
  'Food':      { bg: 'bg-orange-50',   text: 'text-orange-600' },
  'Outdoor':   { bg: 'bg-green-50',    text: 'text-green-600' },
  'Business':  { bg: 'bg-indigo-50',   text: 'text-indigo-600' },
  'Social':    { bg: 'bg-accent-50',   text: 'text-accent-600' },
  'Education': { bg: 'bg-amber-50',    text: 'text-amber-700' },
};
const defaultCategoryColor = { bg: 'bg-primary-50', text: 'text-primary-600' };

// Time filter options
const timeFilters = [
  { label: 'Any time',      value: 'Any time' },
  { label: 'Today',         value: 'Today' },
  { label: 'This Week',     value: 'This week' },
  { label: 'This Month',    value: 'This month' },
];

// Stagger animation config
const gridContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const gridItem = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

function JoinPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    distance: 'Any distance',
    time: 'Any time',
    category: 'All'
  });

  const [selectedCategories, setSelectedCategories] = useState(['All']);
  const [availableCategories, setAvailableCategories] = useState(fallbackCategories);
  const [allEvents, setAllEvents] = useState([]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const eventData = await fetchEvents();
        setAllEvents(eventData);
        const filteredData = applyFilters(eventData, searchQuery);
        setEvents(filteredData);
      } catch (error) {
        console.error('Failed to load events:', error);
        toast.error('Failed to load events. Please try again.');
        setEvents([]);
        setAllEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the real category taxonomy for the filter pills. The previous
  // hardcoded list shared one name with what the API serves, so nearly every
  // pill matched nothing.
  useEffect(() => {
    let ignore = false;
    fetchCategoryNames().then((names) => {
      if (!ignore) setAvailableCategories(names);
    });
    return () => {
      ignore = true;
    };
  }, []);

  // Apply filters whenever filters, categories, or search query change
  useEffect(() => {
    const filteredData = applyFilters(allEvents, searchQuery);
    setEvents(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCategories, allEvents, searchQuery]);

  const applyFilters = (eventData, query = '') => {
    let filteredEvents = [...eventData];

    // Search query filter - searches title, description, and location
    if (query.trim()) {
      const lowerQuery = query.trim().toLowerCase();
      filteredEvents = filteredEvents.filter(event => {
        const title = (event.Title || event.title || '').toLowerCase();
        const description = (event.Description || event.description || '').toLowerCase();
        const location = (event.Location || event.location || '').toLowerCase();
        const city = (event.City || event.city || '').toLowerCase();
        const state = (event.State || event.state || '').toLowerCase();
        return (
          title.includes(lowerQuery) ||
          description.includes(lowerQuery) ||
          location.includes(lowerQuery) ||
          city.includes(lowerQuery) ||
          state.includes(lowerQuery)
        );
      });
    }

    // Apply category filter (multi-select)
    if (!selectedCategories.includes('All') && selectedCategories.length > 0) {
      filteredEvents = filteredEvents.filter(event => {
        const eventCategories = Array.isArray(event.Category) ? event.Category : [event.Category];
        return selectedCategories.some(selectedCat =>
          eventCategories.some(eventCat => {
            const categoryName = typeof eventCat === 'object' && eventCat !== null
              ? (eventCat.name || eventCat.Name || '')
              : String(eventCat || '');
            return categoryName.toLowerCase() === selectedCat.toLowerCase();
          })
        );
      });
    }

    // Apply shared service filters (distance, time, etc.)
    filteredEvents = filterEvents(filteredEvents, filters);

    return filteredEvents;
  };

  const handleJoin = async (eventId) => {
    try {
      await joinEvent(eventId);
      toast.success('Successfully joined!');
      setEvents(prev => prev.map(event =>
        (event.ID || event.id) === eventId
          ? { ...event, isJoined: true, CurrentAttendees: (event.CurrentAttendees || event.current_attendees || 0) + 1 }
          : event
      ));
      setAllEvents(prev => prev.map(event =>
        (event.ID || event.id) === eventId
          ? { ...event, isJoined: true, CurrentAttendees: (event.CurrentAttendees || event.current_attendees || 0) + 1 }
          : event
      ));
    } catch (error) {
      console.error('Failed to join event:', error);
      toast.error('Failed to join event. Please try again.');
    }
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => {
      if (category === 'All') {
        return ['All'];
      }

      let newCategories = prev.filter(cat => cat !== 'All');

      if (newCategories.includes(category)) {
        newCategories = newCategories.filter(cat => cat !== category);
      } else {
        newCategories.push(category);
      }

      return newCategories.length === 0 ? ['All'] : newCategories;
    });
  };

  // Suppress unused variable warning - handleJoin is used by EventCard via event.isJoined state
  void handleJoin;

  return (
    <div className="min-h-screen bg-surface-50">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <h1 className="text-h1 text-surface-900 mb-1">Discover Events</h1>
          <p className="text-body-lg text-surface-500">Find and join CliQQs happening near you</p>
        </motion.div>

        {/* Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="bg-white rounded-2xl shadow-soft-sm border border-surface-100 p-6 mb-6"
        >
          {/* Search input */}
          <div className="relative mb-5">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search events by name, location, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input w-full pl-11 pr-4 py-3 border border-surface-200 rounded-xl text-body text-surface-800 placeholder-surface-400 bg-surface-50 focus:bg-white focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
              aria-label="Search for CliQQ events"
            />
          </div>

          {/* Time filter pills */}
          <div className="flex flex-wrap gap-2">
            {timeFilters.map((tf) => {
              const isActive = filters.time === tf.value;
              return (
                <button
                  key={tf.value}
                  onClick={() => setFilters({ ...filters, time: tf.value })}
                  className={clsx(
                    'px-4 py-2 rounded-xl text-body-sm font-medium transition-all duration-200 border',
                    isActive
                      ? 'bg-primary-500 text-white border-primary-500 shadow-colored'
                      : 'bg-white text-surface-600 border-surface-200 hover:border-primary-300 hover:text-primary-600'
                  )}
                >
                  {tf.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Category Pills */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-body-sm font-semibold text-surface-700">Categories</h3>
            {selectedCategories.length > 0 && !selectedCategories.includes('All') && (
              <button
                onClick={() => setSelectedCategories(['All'])}
                className="text-body-sm text-primary-500 font-medium hover:text-primary-600 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {availableCategories.map((category) => {
              const isSelected = selectedCategories.includes(category);
              const colors = categoryColors[category] || defaultCategoryColor;

              return (
                <button
                  key={category}
                  onClick={() => handleCategoryToggle(category)}
                  className={clsx(
                    'px-3.5 py-1.5 rounded-xl text-body-sm font-medium transition-all duration-200 border',
                    isSelected
                      ? 'bg-primary-500 text-white border-primary-500 shadow-colored'
                      : `${colors.bg} ${colors.text} border-transparent hover:shadow-soft-xs`
                  )}
                >
                  {category}
                  {isSelected && category !== 'All' && (
                    <svg className="inline-block w-3.5 h-3.5 ml-1.5 -mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          {selectedCategories.length > 0 && !selectedCategories.includes('All') && (
            <p className="mt-2 text-caption text-surface-500">
              Selected: {selectedCategories.join(', ')}
            </p>
          )}
        </motion.div>

        {/* Events Section */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-h2 text-surface-900">
              All Events
              {!loading && (
                <span className="text-body-sm text-surface-400 font-normal ml-2">({events.length})</span>
              )}
            </h2>
          </div>

          {/* Loading skeleton */}
          {loading && <EventGridSkeleton count={6} />}

          {/* Empty state */}
          {!loading && events.length === 0 && (
            <EmptyState
              icon={
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
              title="No events found"
              description={
                searchQuery
                  ? `No events match "${searchQuery}". Try a different search or clear your filters.`
                  : 'Be the first to create a CliQQ!'
              }
              actionLabel="Create a CliQQ"
              actionTo="/create-event"
            />
          )}

          {/* Event Grid */}
          {!loading && events.length > 0 && (
            <motion.div
              variants={gridContainer}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
            >
              {events.map((event) => {
                const eventId = event.ID || event.id;
                return (
                  <motion.div key={eventId} variants={gridItem}>
                    <EventCard event={event} />
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>

      </div>
    </div>
  );
}

export default JoinPage;
